'use client';

import { useEffect, useRef } from 'react';
import { sameCartProduct } from '@/lib/cart-product-snapshot';
import { useProducts } from './use-products';
import { useCartStore, selectCartItems, selectHasHydrated } from '@/stores/cart-store';
import { getProductById } from '@/lib/api/products';
import { toast } from '@/components/ui/feedback/toast';

const RUB = new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 });

/**
 * Показывает клиенту тост о смене цены — без сюрприза на платёжке.
 * Группируем все изменения в одно сообщение, чтобы не флудить.
 */
function notifyPriceChanges(changes: Array<{ name: string; oldPrice: number; newPrice: number }>) {
  if (changes.length === 0) return;
  if (changes.length === 1) {
    const c = changes[0];
    toast.info(
      `Цена обновилась: «${c.name}» ${RUB.format(c.oldPrice)} → ${RUB.format(c.newPrice)}`
    );
    return;
  }
  const totalDelta = changes.reduce((s, c) => s + (c.newPrice - c.oldPrice), 0);
  toast.info(
    `Обновлены цены ${changes.length} товаров в корзине (итог ${totalDelta > 0 ? '+' : ''}${RUB.format(totalDelta)})`
  );
}

// Синхронизирует item.product в persisted cart-store со свежими данными из API.
// Why: localStorage хранит снапшот product целиком. Если товар добавили давно, до того
// как у него появился sku в БД (или до фикса нормализации цены) — снапшот протухает,
// и чекаут шлёт на бэк sku: "" → orders.js отвечает 400 "Некоторые товары не имеют SKU".
// Дополнительно: товары с is_visible=0 (категория «Роллы -30%») в каталог не попадают,
// для них нужен точечный fallback через getProductById(id, includeHidden=true).
// How to apply: монтируется один раз в Providers, реагирует на hydration cart-store.
export function useCartProductSync() {
  const hasHydrated = useCartStore(selectHasHydrated);
  const items = useCartStore(selectCartItems);
  const setItems = useCartStore((s) => s.setItems);

  // Дёргаем общий кэш каталога. На главной он уже прогрет — здесь будет cache hit.
  // На страницах без useProducts() запрос всё равно нужен, иначе синхронизация не сработает.
  const hasNonBonusItems = items.some((it) => !it.isBonus && !it.bonusType);
  const { data: products } = useProducts({
    enabled: hasHydrated && hasNonBonusItems,
  });

  // Защита от повторного запроса одних и тех же скрытых товаров —
  // храним id'ы, по которым уже сделали fallback в текущей сессии хука.
  const hiddenFetchedRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!hasHydrated || items.length === 0) return;

    // products может быть undefined пока useProducts ещё не вернул данные.
    // В этом случае не делаем синк (избегаем фантомных обновлений).
    if (!products) return;

    const productMap = new Map(products.map((p) => [p.id, p]));
    let changed = false;

    // ── Шаг 1: синк из общего каталога (мгновенный, без сети) ──
    const priceChanges: Array<{ name: string; oldPrice: number; newPrice: number }> = [];
    const refreshed = items.map((item) => {
      if (item.isBonus || item.bonusType) return item;

      const fresh = productMap.get(item.id);
      if (!fresh) return item; // нет в каталоге — шаг 2 заберёт точечно

      const stale = item.product;
      const priceChanged = Number(fresh.price) !== Number(stale.price);

      if (priceChanged) {
        priceChanges.push({
          name: stale.name,
          oldPrice: Number(stale.price),
          newPrice: Number(fresh.price),
        });
      }

      if (!sameCartProduct(stale, fresh)) {
        changed = true;
        return { ...item, product: fresh };
      }
      return item;
    });

    if (changed) {
      setItems(refreshed);
      notifyPriceChanges(priceChanges);
    }

    // ── Шаг 2: для товаров не из публичного каталога — точечный запрос ──
    // Это покрывает is_visible=0 товары (категория «Роллы -30%» id=15, скрытые сэты,
    // спец-акции и т.п.). Без этого их product.price в корзине остаётся stale.
    const hiddenIds = items
      .filter((it) => !it.isBonus && !it.bonusType && !productMap.has(it.id))
      .map((it) => it.id)
      .filter((id) => !hiddenFetchedRef.current.has(id));

    if (hiddenIds.length === 0) return;

    // Помечаем что начали загрузку — чтобы не повторить
    hiddenIds.forEach((id) => hiddenFetchedRef.current.add(id));

    Promise.allSettled(
      hiddenIds.map((id) => getProductById(id, /* includeHidden */ true))
    ).then((results) => {
      const fetched = new Map<number, NonNullable<ReturnType<typeof productMap.get>>>();
      results.forEach((r, i) => {
        if (r.status === 'fulfilled' && r.value) {
          fetched.set(hiddenIds[i], r.value);
        }
      });
      if (fetched.size === 0) return;

      // Применяем к актуальному состоянию через свежий read из store —
      // items в этом колбэке мог устареть.
      const currentItems = useCartStore.getState().items;
      let inner = false;
      const innerPriceChanges: Array<{ name: string; oldPrice: number; newPrice: number }> = [];
      const next = currentItems.map((item) => {
        if (item.isBonus || item.bonusType) return item;
        const fresh = fetched.get(item.id);
        if (!fresh) return item;
        const stale = item.product;
        const same = sameCartProduct(stale, fresh);
        if (same) return item;

        if (Number(fresh.price) !== Number(stale.price)) {
          innerPriceChanges.push({
            name: stale.name,
            oldPrice: Number(stale.price),
            newPrice: Number(fresh.price),
          });
        }

        inner = true;
        return { ...item, product: fresh };
      });
      if (inner) {
        setItems(next);
        notifyPriceChanges(innerPriceChanges);
      }
    }).catch(() => {
      // Сетевая ошибка — не падаем, в следующий mount хук попробует снова
      // (после очистки hiddenFetchedRef через перезагрузку страницы).
    });
  }, [hasHydrated, products, items, setItems]);
}

// Компонент-обёртка: позволяет дёргать хук в Providers без условного рендеринга.
export function CartProductSync() {
  useCartProductSync();
  return null;
}
